import { Injectable, inject } from '@angular/core';
import { AES, enc } from 'crypto-js';
import { orderBy, pick } from 'lodash-es';

import { StorageService } from '@app/shared/browser/storage.service';
import type { OBJECTS } from '@app/shared/common/shared.constant';
import type { GlobalSearchItem } from '@app/theme/layouts/header-nav/global-search/global-search.component';

@Injectable({
    providedIn: 'root',
})
export class RecentActivityService {
    private readonly BASE_STORAGE_KEY = 'recentActivitiesGGX';

    // Maximum number of activities to store
    private readonly MAX_ENTRIES = 20;

    private readonly storageService = inject(StorageService);

    private readonly SECRET_KEY =
        'sayRnkrPOW9fSTgo1psDX2IBvLIjz9N8Sd9N0MHqTns1BN//BHBfmVMiXBv9+DQ0';

    private getStorageKey(): string {
        return `${this.BASE_STORAGE_KEY}_0`;
    }

    private safeSetItem(key: string, value: string): void {
        try {
            localStorage.setItem(key, value);
        } catch (error) {
            if (error instanceof DOMException && error.name === 'QuotaExceededError') {
                // If we hit the quota, try to reduce our service's data
                let activities = this.getRecentActivities();

                // Parse the new activity from the value
                const newActivity = JSON.parse(
                    AES.decrypt(value, this.SECRET_KEY).toString(enc.Utf8),
                )[0];

                // Add the new activity to the list
                activities.unshift(newActivity);

                // Keep reducing by half until we can store the data
                let success = false;
                while (activities.length > 1 && !success) {
                    // Remove the oldest half of activities
                    activities = activities.slice(0, Math.ceil(activities.length / 2));

                    try {
                        const reducedData = AES.encrypt(
                            JSON.stringify(activities),
                            this.SECRET_KEY,
                        ).toString();
                        this.storageService.setLocalStorage(key, reducedData);
                        success = true;
                    } catch (_retryError) {
                        // Do nothing
                    }
                }

                if (!success) {
                    // If we still can't store, try to clear only this workspace's data
                    this.clearWorkspaceData();
                    try {
                        const finalData = AES.encrypt(
                            JSON.stringify(activities),
                            this.SECRET_KEY,
                        ).toString();
                        this.storageService.setLocalStorage(key, finalData);
                    } catch (_clearError) {
                        // If we still can't store, give up but don't prevent other operations
                        console.warn('Failed to store recent activities after clearing data');
                    }
                }
            } else {
                console.error('Error storing data:', error);
            }
        }
    }

    private clearWorkspaceData(): void {
        localStorage.removeItem(this.getStorageKey());
    }

    /**
     * Retrieves recent activities
     * Activities are decrypted and sorted by timestamp in descending order.
     * @returns Array of recent activities, sorted by timestamp
     */
    getRecentActivities(): { objectType: OBJECTS; timestamp: string; details: GlobalSearchItem }[] {
        try {
            const encryptedData = this.storageService.getLocalStorage(this.getStorageKey());

            if (!encryptedData) {
                return [];
            }

            const bytes = AES.decrypt(encryptedData, this.SECRET_KEY);
            const decryptedData = bytes.toString(enc.Utf8);

            return orderBy(JSON.parse(decryptedData), 'timestamp', ['desc']);
        } catch (error) {
            console.error('Error retrieving recent activities:', error);

            return [];
        }
    }

    /**
     * Adds a new activity to the recent activities list.
     * If an activity with the same objectType and details.id exists, it will be updated.
     * The list is maintained at a maximum size of MAX_ENTRIES.
     * @param activity - The activity to add or update
     */
    addActivity(activity: {
        objectType: OBJECTS;
        timestamp: string;
        details: GlobalSearchItem;
    }): void {
        const propertiesToStore: (keyof GlobalSearchItem)[] = [
            'id',
            'name',
            'createdByVisibleName',
            'createdBy',
            'createdDate',
            'currentStatus',
            'objectType',
            'version',
            'projectSlug',
        ];

        activity = {
            ...activity,
            // Pick the properties that are needed to display the activity in the recent activities list
            details: pick(activity.details, propertiesToStore),
        };

        try {
            let activities = this.getRecentActivities();

            // Find and replace existing activity or add new one
            const existingIndex = activities.findIndex(
                existing =>
                    existing.objectType === activity.objectType &&
                    existing.details.id === activity.details.id,
            );

            if (existingIndex !== -1) {
                // Replace the existing activity with the new one
                activities[existingIndex] = activity;
            } else {
                // Add the new activity at the start of the list
                activities.unshift(activity);
            }

            // Ensure the list doesn't exceed the max allowed entries
            if (activities.length > this.MAX_ENTRIES) {
                activities = activities.slice(0, this.MAX_ENTRIES);
            }

            // Encrypt and store back to localStorage
            const encryptedData = AES.encrypt(
                JSON.stringify(activities),
                this.SECRET_KEY,
            ).toString();
            this.safeSetItem(this.getStorageKey(), encryptedData);
        } catch (error) {
            console.error('Error adding recent activity:', error);
        }
    }

    /**
     * Deletes a specific activity from the recent activities list.
     * @param objectType - The type of object the activity is associated with
     * @param id - The ID of the object the activity is associated with
     */
    deleteActivity(objectType: OBJECTS, id: number): void {
        try {
            let activities = this.getRecentActivities();

            // Filter out the activity that matches the given objectType and id
            activities = activities.filter(
                activity => !(activity.objectType === objectType && activity.details.id === id),
            );

            // Encrypt and store back to localStorage
            const encryptedData = AES.encrypt(
                JSON.stringify(activities),
                this.SECRET_KEY,
            ).toString();
            this.safeSetItem(this.getStorageKey(), encryptedData);
        } catch (error) {
            console.error('Error deleting recent activity:', error);
        }
    }

    deleteAllActivities(): void {
        this.storageService.setLocalStorage(this.getStorageKey(), '');
    }
}
